import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { ErrorM } from '../../components/formComponents/ErrorM';
import { Dropzone } from "./Dropzone";

export interface FormDropzoneFieldProps {
  field: any;
  label: string;
  readOnly?: boolean;
  onFileUploadEnd(file: File): void;
  file: any;
  fileInstructions?: string;
  maximumFileSizeInMB?: number;
  maximumNoOfFiles?: number;
  fileType?: string;
  dropzoneText?: string;
  uploadIcon?: string;
  warningIcon?: string;
  showUploadedImage?: any;
  uploadedImageUrl?: string;
  fileName?: string;
  acceptedTypes?: string[] | null;
  onlyShowFileName?: boolean;
  alternativeErrMsg?: boolean;
}

function FileUploadPanel<T>(props: FormDropzoneFieldProps) {
  const formikContext = useFormikContext();
  const [showWarningMessage, setShowWarningMessage] = React.useState<
    string | null
  >();
  const [showAltWarningMessage, setShowAltWarningMessage] = React.useState<
    string | null
  >();  

  useEffect(() => {
    props.showUploadedImage && props.showUploadedImage();
  }, [props.showUploadedImage, props.uploadedImageUrl]);

  useEffect(() => {
    setShowAltWarningMessage(null);
  }, [props.file]);  

  function setFileType(chosen_file: File) {
    if (chosen_file) {
      if (!props.fileType) {
        if (isImage(chosen_file)) {
          return "image";
        } else if (isVideo(chosen_file)) {
          return "video";
        } else if (isDoc(chosen_file)) {
          return "document";
        }
      } else {
        return props.fileType.toLowerCase();
      }
    }
  }

  const onDrop = (acceptedFiles: File[]) => {
    validateFiles(acceptedFiles);
  };

  function validateFiles(acceptedFiles: File[]) {
    let filesValPassed = true;

    if (filesValPassed && !acceptedFiles[0]) {
      setDropzoneWarningMsg("An error occured, please try again");
      filesValPassed = false;
    }

    if(setFileType(acceptedFiles[0]) === "video"){
      if(fileExt(acceptedFiles[0]) !== "mp4"){
        setDropzoneWarningMsg("Please only upload an mp4 video");
        filesValPassed = false;
      }
    }

    if (
      filesValPassed &&
      props.maximumNoOfFiles &&
      acceptedFiles.length > props.maximumNoOfFiles
    ) {
      const pluralTxtMsg =
        props.maximumNoOfFiles > 1
          ? "Please only upload up to " + props.maximumNoOfFiles + " files"
          : "Please only upload one file";
      setDropzoneWarningMsg(pluralTxtMsg);
      filesValPassed = false;
    }

    if (filesValPassed && props.fileType) {
      let isFileTypeAllowed =
        setFileType(acceptedFiles[0]) === "image"
          ? isImage
          : setFileType(acceptedFiles[0]) === "video"
          ? isVideo
          : isDoc;

      if (!isFileTypeAllowed(acceptedFiles[0])) {
        setDropzoneWarningMsg(
          "Please upload only " + props.fileType?.toLocaleLowerCase() + " files"
        );
        filesValPassed = false;
      }
    }

    if (
      filesValPassed &&
      props.maximumFileSizeInMB &&
      acceptedFiles[0].size / 1000000 >= props.maximumFileSizeInMB
    ) {
      setDropzoneWarningMsg(
        "File exceeds " + props.maximumFileSizeInMB + "MB size limit"
      );
      filesValPassed = false;
    }

    completeFileValidation(filesValPassed, acceptedFiles);
  }

  const setDropzoneWarningMsg = (warning_str: string) => {
    if(props.alternativeErrMsg){
      setShowAltWarningMessage(warning_str);
    } else {
      setShowWarningMessage(warning_str);
      setTimeout(() => {
        setShowWarningMessage(null);
      }, 2500);
    }
  };

  function completeFileValidation(
    file_validated: boolean,
    acceptedFiles: File[]
  ) {
    if (file_validated) {
      props.onFileUploadEnd(acceptedFiles[0]);
    } else {
      return;
    }
  }

  return (
    <div className="FormBox FormSideBySide">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <Dropzone
          uploadedFile={props.file}
          onDrop={onDrop}
          fileType={setFileType(props.file)}
          readOnly={props.readOnly ?? false}
          dropzoneText={props.dropzoneText}
          uploadIcon={props.uploadIcon}
          warningIcon={props.warningIcon}
          showWarningMessage={showWarningMessage}
          showAltWarningMessage={showAltWarningMessage}
          hasUploadedImage={props.uploadedImageUrl ? true : false}
          uploadedImageUrl={props.uploadedImageUrl}
          fileName={props.fileName}
          acceptedTypes={props.acceptedTypes}
          onlyShowFileName={props.onlyShowFileName}
        />
        {props.fileInstructions && (
          <span className="FileInstructions">{props.fileInstructions}</span>
        )}
        <ErrorM name={props.field} />
      </div>
    </div>
  );
}

export default FileUploadPanel;

const isImage = (file: File) => file.type.search("image") > -1;
const isVideo = (file: File) => file.type.search("video") > -1;
const fileExt = (file: File) => file.name.split('.')[1];
const isDoc = (file: File) => {
  if (
    file.type === "application/pdf" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.type === "application/msword" ||
    file.type === "text/csv"
  ) {
    return true;
  } else {
    return false;
  }
};
